import { useState, useEffect } from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import api from '../../../services/api';

import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo
} from '../../../hooks/useDateTimeFilter';



import useAuth from "../../../hooks/useAuth";
import AssessmentsCountByTypeChart from "../../../components/quality/charts/AssessmentsCountByTypeChart";
import AssessmentsDynamicsCountByOperatorChart
  from "../../../components/quality/charts/AssessmentsDynamicsCountByOperatorChart";
import AssessmentsByOperatorsChart from "../../../components/quality/charts/AssessmentsByOperatorsChart";
import AssessmentsByCriteriaChart from "../../../components/quality/charts/AssessmentsByCriteriaChart";

const AssessmentDynamicsChartsPage = () => {
  const { projects } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({});
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[pathParts.length - 1];
  const project = projects.find(p => p.id === parseInt(projectId));

  // Добавляем фильтры даты
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;
  useEffect(() => {
    // Находим проект по ID из последнего сегмента URL
    const project = projects.find(p => p.id === parseInt(projectId));
    setSelectedProject(project);
  }, [projects, projectId]);

  // Устанавливаем дефолтные значения дат: 30 дней назад и завтра
  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedProject) return;

      try {
        setLoading(true);

        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        // Загружаем месяцы для выпадающего списка
        const monthsResponse = await api.get(`/quality/assessment-months`, {
          params: { project_id: selectedProject.id }
        });
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }

        // Извлекаем month и year из selectedMonthYear
        let month = '';
        let year = '';
        if (selectedMonthYear) {
          const [m, y] = selectedMonthYear.split('-');
          month = m;
          year = y;
        }

        // Загружаем оценки для выбранного проекта
        const assessmentsResponse = await api.get(`/quality/assessments`, {
          params: {
            project_id: selectedProject.id,
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });
        setAssessments(assessmentsResponse.data.data || []);

        // Загружаем все опции полей для проекта
        const optionsResponse = await api.get(`/field-options/all?project_id=${selectedProject.id}`);
        // Загружаем операторов
        const operatorsResponse = await api.get(`/operators?project_id=${selectedProject.id}`);
        let operatorsList = operatorsResponse.data.operators || [];

        // Проверяем, есть ли в assessments agent_name, которых нет в operatorsList
        const uniqueAgentNames = [...new Set(assessments.map(a => a.agent_name).filter(Boolean))];

        uniqueAgentNames.forEach(agentName => {
          // Проверяем, есть ли уже такой оператор в списке
          const exists = operatorsList.some(op =>
            `${op.surname} ${op.name}` === agentName
          );

          if (!exists) {
            // Разделяем имя на фамилию и имя
            const nameParts = agentName.trim().split(' ');
            if (nameParts.length >= 2) {
              const surname = nameParts[0];
              const name = nameParts[1];

              operatorsList.push({
                id: -1, // временный ID для отсутствующих в базе операторов
                surname: surname,
                name: name
              });
            }
          }
        });

        if (optionsResponse.data.success) {
          setFieldOptions(prev => ({
            ...optionsResponse.data.data,
            operators: operatorsList
          }));
        }

        // Загружаем все критерии проекта
        const criteriaResponse = await api.get(`/quality/criteria?project_id=${selectedProject.id}`);
        if (criteriaResponse.data.success) {
          setCriteria(criteriaResponse.data.data || []);
        }

        if (assessmentsResponse.data && assessmentsResponse.data.filters) {
          let shouldUpdate = false;

          const serverFrom = assessmentsResponse.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = assessmentsResponse.data.filters.date_filter_to || getDefaultDateTo();

          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          // Проверяем, нужно ли обновить фильтры
          if (inputFrom && inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
            shouldUpdate = true;
          }
          if (inputTo && inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
            shouldUpdate = true;
          }

          // Обновляем данные, если нужно
          if (shouldUpdate) {
            setAssessments(assessmentsResponse.data.data || []);
            return;
          }

          // Сбрасываем фильтр по месяцу, если используется фильтр по датам
          if (assessmentsResponse.data.filters.date_filter_from && assessmentsResponse.data.filters.date_filter_to) {
            setSelectedMonthYear('');
          }

          // Обновляем выбранный месяц и год из фильтров
          if (assessmentsResponse.data.filters.month && assessmentsResponse.data.filters.year) {
            const monthYearValue = `${assessmentsResponse.data.filters.month}-${assessmentsResponse.data.filters.year}`;
            if (monthYearValue !== selectedMonthYear) {
              setSelectedMonthYear(monthYearValue);
            }
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [selectedProject, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);
  // Функция для получения названия месяца по номеру
  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthNumber - 1] || '';
  };

  // Формируем опции для выпадающего списка
  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  // Функция для скачивания блока графиков
  const handleDownloadChart = async (rowType) => {
    let chartContainer;

    // Находим нужный контейнер блока графиков
    switch(rowType) {
      case 'first-row':
        chartContainer = document.querySelector('.charts-row:nth-child(1)');
        break;
      case 'second-row':
        chartContainer = document.querySelector('.charts-row:nth-child(2)');
        break;
      default:
        return;
    }

    if (!chartContainer) return;

    try {
      // Ждем полной отрисовки диаграмм
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Получаем полные размеры контейнера
      const chartRect = chartContainer.getBoundingClientRect();
      const fullWidth = Math.ceil(chartRect.width);
      const fullHeight = Math.ceil(chartRect.height);

      // Создаем новое canvas для объединенного изображения
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = fullWidth * 2;
      combinedCanvas.height = fullHeight * 2;

      const ctx = combinedCanvas.getContext('2d');
      ctx.scale(2, 2);

      // Заполняем фон
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fullWidth, fullHeight);

      // Получаем все canvas элементы в контейнере
      // const canvases = chartContainer.querySelectorAll('canvas');

      // Получаем все canvas элементы в контейнере
      const canvases = Array.from(chartContainer.querySelectorAll('canvas'));

      if (canvases.length >= 2) {
        // Получаем позиции левой и правой диаграмм
        const leftCanvas = canvases[0];
        const rightCanvas = canvases[1];

        const leftRect = leftCanvas.getBoundingClientRect();
        const rightRect = rightCanvas.getBoundingClientRect();

        // Вычисляем центральную линию между диаграммами
        const centerX = (leftRect.right + rightRect.left) / 2 - chartRect.left;

        // Рисуем вертикальную линию между диаграммами
        ctx.beginPath();
        ctx.moveTo(centerX, 0);
        ctx.lineTo(centerX, fullHeight);
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Для каждого canvas элемента копируем его содержимое
      canvases.forEach(canvas => {
        const rect = canvas.getBoundingClientRect();
        const x = rect.left - chartRect.left;
        const y = rect.top - chartRect.top;

        // Создаем временный canvas для корректного копирования
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');

        // Копируем содержимое оригинального canvas в временный
        tempCtx.drawImage(canvas, 0, 0);

        // Копируем из временного canvas в итоговый
        ctx.drawImage(tempCanvas, x, y, canvas.width, canvas.height);
      });

      // Получаем все текстовые элементы и заголовки
      const headers = chartContainer.querySelectorAll('h3, .bar-label, .x-axis-label, .no-data');
      headers.forEach(header => {
        const rect = header.getBoundingClientRect();
        const x = rect.left - chartRect.left;
        const y = rect.top - chartRect.top;
        const style = window.getComputedStyle(header);

        ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        ctx.fillStyle = style.color;
        ctx.textAlign = style.textAlign || 'left';
        ctx.fillText(header.textContent, x, y + parseInt(style.fontSize));
      });

      // Создаем ссылку для скачивания
      const link = document.createElement('a');
      link.download = `блок_графиков_${rowType}_${new Date().toISOString().slice(0,10)}.png`;
      link.href = combinedCanvas.toDataURL('image/png');
      link.click();

    } catch (error) {
      console.error('Ошибка при создании изображения:', error);
      alert('Не удалось создать изображение. Попробуйте еще раз.');
    }
  };

  if (loading) return <div className="p-4">Загрузка данных для графиков...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
      <div className="main-content">
        <h1>Графики аналитики по оценкам</h1>
        {loading ? (
            <div>Загрузка...</div>
        ) : error ? (
            <div className="error">{error}</div>
        ) : (
            <div>
              <p>Проект: {selectedProject ? selectedProject.name_text : 'Не выбран'}</p>
              <p>Загружено оценок: {assessments.length}</p>

              {/* Фильтры даты */}
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <label className="form-label">Дата от (с секундами)</label>
                  <input
                      type="datetime-local"
                      name="date_filter_from"
                      className="form-control form-control-sm"
                      value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                      onChange={handleDateChange}
                      onBlur={handleDateBlur}
                      step="1"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Дата до (с секундами)</label>
                  <input
                      type="datetime-local"
                      name="date_filter_to"
                      className="form-control form-control-sm"
                      value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                      onChange={handleDateChange}
                      onBlur={handleDateBlur}
                      step="1"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Фильтр по месяцу</label>
                  <select
                      className="form-control form-control-sm"
                      value={selectedMonthYear}
                      onChange={(e) => setSelectedMonthYear(e.target.value)}
                  >
                    <option value="">Все месяцы</option>
                    {monthYearOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="charts-container">
                <div className="charts-row">
                  <div className="chart-card w-50">
                    <AssessmentsCountByTypeChart
                      assessments={assessments}
                      fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="chart-card w-50">
                    <AssessmentsDynamicsCountByOperatorChart
                      assessments={assessments}
                      fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadChart('first-row')}
                    >
                      Скачать блок графиков
                    </button>
                  </div>
                </div>

                <div className="charts-row">
                  <div className="chart-card w-50">
                    <AssessmentsByOperatorsChart
                      assessments={assessments}
                      fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="chart-card w-50">
                    <AssessmentsByCriteriaChart
                      assessments={assessments}
                      criteria={criteria}
                      fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadChart('second-row')}
                    >
                      Скачать блок графиков
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  )

}

export default AssessmentDynamicsChartsPage;

